import React, { useState, useEffect } from "react";
import {
  Modal,
  Alert,
  Checkbox,
  Form,
  Input,
  Radio,
  Card,
  Divider,
  Steps,
  Select,
  Tag,
  Image,
  Row,
  Col,
  List,
  Avatar,
  DatePicker,
  Dropdown,
  Button,
  Menu,
  Typography,
  Space,
  Badge,
  Spin,
} from "antd";
import "./VoucherBooking.css";
import {
  ShoppingCartOutlined,
  CalendarOutlined,
  DownOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import { observer } from "mobx-react-lite";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { useAuth } from "../../../layouts/auth/AuthContext";
import { mstBooking, mstGeneral } from "../../../mobx";
import Navbar from "../../../layouts/navbar";
import { useVoucher } from "./VoucherContext";
import Paragraph from "antd/lib/typography/Paragraph";
import { useCart } from "../../../layouts/cart/CartContext";
import Footer from "../../../layouts/footer";
import { useReferral } from "../../../utils/referral";

const { Text, Link, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

const VoucherDetails = observer((props: any) => {
  const location = useLocation();
  const { id } = useParams();
  const {
    vouchers,
    fetchVouchers,
    fetchVoucherDetails,
    voucherDetails,
    onCurrentBooking,
    addToCart,
    loading,
  } = useVoucher();
  const { user } = useAuth();
  const { showCart } = useCart();
  const { navigateWithReferral } = useReferral();

  const [selectedPrice, setSelectedPrice] = useState<any>(null);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  useEffect(() => {
    if (id) {
      fetchVoucherDetails(Number(id));
    }
  }, [id]);

  // Reset selections when voucher details change
  useEffect(() => {
    if (voucherDetails) {
      setSelectedPrice(null);
      setQuantity(1);
    }
  }, [voucherDetails]);

  const onAddToCart = async (
    voucher: any,
    selectedOption: any,
    quantity = 1
  ) => {
    setIsAddingToCart(true);

    try {
      const newItem: any = {
        voucher_name: voucher?.title,
        voucher_id: voucher?.voucher_id,
        store_name: voucher?.store_name,
        store_id: voucher?.store_id,
        variant: {
          ...selectedOption,
          quantity: quantity,
        },
      };

      await addToCart(newItem);

      Modal.success({
        title: "Added to Cart",
        content: `${voucher?.title} has been added to your cart.`,
        okText: "View Cart",
        cancelText: "Continue Shopping",
        onOk: () => showCart(`${location.pathname}`),
      });
    } catch (error) {
      Modal.error({
        title: "Error",
        content: "Failed to add voucher to cart. Please try again.",
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  const increaseQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  // Calculate total price
  const calculateTotalPrice = (): number => {
    const price = selectedPrice
      ? parseFloat(selectedPrice.amount)
      : voucherDetails?.amount
      ? parseFloat(voucherDetails.amount)
      : 0;

    return price * quantity;
  };

  // Check if required selections are satisfied
  const areRequiredSelectionsSatisfied = (): boolean => {
    if (!voucherDetails) return false;

    // If voucher has price categories, one must be selected
    if (voucherDetails.price_cat && voucherDetails.price_cat.length > 0) {
      return selectedPrice !== null;
    }

    // If no price categories, always satisfied
    return true;
  };

  // Handle add to cart with validation
  const handleAddToCart = () => {
    if (!voucherDetails) return;

    if (!areRequiredSelectionsSatisfied()) {
      Modal.warning({
        title: "Selection Required",
        content: "Please select a category before adding to cart.",
      });
      return;
    }

    let finalSelectedPrice: any = selectedPrice;

    // If voucher has no variants, create a default price object
    if (!voucherDetails.price_cat || voucherDetails.price_cat.length === 0) {
      finalSelectedPrice = {
        price_id: voucherDetails.voucher_id?.toString() || "default",
        title: "Standard",
        amount: voucherDetails.amount,
        quantity: quantity,
      };
    } else {
      // For variant vouchers, include the quantity
      finalSelectedPrice = {
        ...finalSelectedPrice,
        quantity: quantity,
      };
    }

    onAddToCart(voucherDetails, finalSelectedPrice, quantity);
  };

  if (loading) {
    return (
      <div id="HomeScreen">
        <div className="container">
          <Navbar hideBanner={true} />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <Spin size="large" />
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  if (!voucherDetails) {
    return (
      <div id="HomeScreen">
        <div className="container">
          <Navbar />
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Title level={3}>Voucher Not Found</Title>
            <Button type="primary" onClick={() => navigateWithReferral(-1)}>
              Go Back
            </Button>
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  const hasVariants =
    voucherDetails.price_cat && voucherDetails.price_cat.length > 0;
  const basePrice = selectedPrice
    ? parseFloat(selectedPrice.amount)
    : voucherDetails.amount
    ? parseFloat(voucherDetails.amount)
    : 0;
  const totalPrice = calculateTotalPrice();

  return (
    <div id="HomeScreen">
      <div className="container">
        <Navbar hideBanner />

        <Row gutter={[24, 24]} style={{ marginBottom: 20 }}>
          {/* Voucher Image - 16:9 Aspect Ratio */}
          <Col xs={24} lg={14}>
            <div
              style={{
                width: "100%",
                aspectRatio: "16 / 9", // 16:9 for voucher images
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f5f5f5",
                borderRadius: "12px",
                marginBottom: "16px",
              }}
            >
              <img
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                }}
                src={voucherDetails.image_url}
                alt={voucherDetails.title}
                onError={(e) => {
                  e.currentTarget.src = "/placeholder-image.png";
                }}
              />
            </div>

            {/* Description Section */}
            {voucherDetails.description && (
              <Card style={{ marginTop: 24 }} bodyStyle={{ padding: 0 }}>
                <div style={{ padding: 24 }}>
                  <Title level={4}>About This Voucher</Title>
                  <Divider style={{ margin: "16px 0" }} />
                  <div
                    style={{
                      lineHeight: "1.6",
                      color: "#666",
                      fontSize: "14px",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: voucherDetails.description,
                    }}
                  />
                </div>
              </Card>
            )}
          </Col>

          {/* Voucher Details & Booking */}
          <Col xs={24} lg={10}>
            <div style={{ position: "sticky", top: "100px" }}>
              {/* Voucher Title */}
              <div style={{ marginBottom: 16 }}>
                <Title level={2} style={{ marginBottom: 8 }}>
                  {voucherDetails.title}
                </Title>

                {/* Store Info */}
                {voucherDetails.store_name && (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ color: "#666" }}>
                      {voucherDetails.store_name}
                    </Text>
                  </div>
                )}
              </div>

              {/* Price Display */}
              {!hasVariants && (
                <div style={{ marginBottom: 24 }}>
                  <Text strong style={{ fontSize: 28, color: "#e90056" }}>
                    RM {basePrice.toFixed(2)}
                  </Text>
                  {voucherDetails.app_promotion_price &&
                    voucherDetails.app_promotion_price !== "0.00" && (
                      <div style={{ marginTop: 4 }}>
                        <Text
                          type="secondary"
                          style={{
                            textDecoration: "line-through",
                            marginRight: 8,
                            fontSize: 16,
                          }}
                        >
                          RM {voucherDetails.amount}
                        </Text>
                        <Text strong style={{ color: "#ff4d4f", fontSize: 16 }}>
                          Promo: RM {voucherDetails.app_promotion_price}
                        </Text>
                      </div>
                    )}
                </div>
              )}

              <Divider />

              {/* Category Selection */}
              {hasVariants && (
                <>
                  <div style={{ marginBottom: 24 }}>
                    <Title level={5} style={{ marginBottom: 16 }}>
                      Choose Category
                    </Title>
                    <Radio.Group
                      onChange={(e) => {
                        setSelectedPrice(e.target.value);
                        setQuantity(1); // Reset quantity when category changes
                      }}
                      value={selectedPrice}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      {voucherDetails.price_cat.map((cat: any) => (
                        <Radio
                          key={cat.price_id}
                          value={cat}
                          style={{
                            padding: "16px",
                            border: `1px solid ${
                              selectedPrice?.price_id === cat.price_id
                                ? "#1890ff"
                                : "#d9d9d9"
                            }`,
                            borderRadius: "8px",
                            backgroundColor:
                              selectedPrice?.price_id === cat.price_id
                                ? "#f0f8ff"
                                : "white",
                            transition: "all 0.3s",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              gap: 20,
                            }}
                          >
                            <div>
                              <Text
                                strong={
                                  selectedPrice?.price_id === cat.price_id
                                }
                              >
                                {cat.title}
                              </Text>
                            </div>
                            <div>
                              <Text strong style={{ color: "#e90056" }}>
                                {`RM ${cat.amount}`}
                              </Text>
                            </div>
                          </div>
                        </Radio>
                      ))}
                    </Radio.Group>
                  </div>
                  <Divider />
                </>
              )}

              {/* Quantity Selector */}
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>Quantity</Title>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {/* <Button.Group> */}
                  <Button
                    icon={
                      <MinusOutlined
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    }
                    onClick={decreaseQuantity}
                    disabled={quantity <= 1}
                    size="large"
                  />
                  <Button style={{ minWidth: 60, fontSize: 16 }} size="large">
                    {quantity}
                  </Button>
                  <Button
                    icon={
                      <PlusOutlined
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    }
                    onClick={increaseQuantity}
                    size="large"
                  />
                  {/* </Button.Group> */}
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    {quantity} voucher(s)
                  </Text>
                </div>
              </div>

              {/* Total Price Display */}
              <div
                style={{
                  padding: "16px",
                  background: "#f9f9f9",
                  borderRadius: "8px",
                  marginBottom: 24,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text strong style={{ fontSize: 16 }}>
                    Total Amount:
                  </Text>
                  <Text strong style={{ fontSize: 20, color: "#e90056" }}>
                    RM {totalPrice.toFixed(2)}
                  </Text>
                </div>
                {quantity > 1 && (
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 12,
                      display: "block",
                      textAlign: "right",
                    }}
                  >
                    (RM {basePrice.toFixed(2)} per voucher)
                  </Text>
                )}
              </div>

              {/* Add to Cart Button */}
              <Button
                type="primary"
                size="large"
                icon={
                  <ShoppingCartOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
                loading={isAddingToCart}
                onClick={handleAddToCart}
                disabled={!areRequiredSelectionsSatisfied()}
                style={{
                  width: "100%",
                  height: "50px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  backgroundColor: "#e90056",
                  borderColor: "#e90056",
                  color: "#fff",
                }}
              >
                {isAddingToCart ? "Adding to Cart..." : "Add to Cart"}
              </Button>

              {!areRequiredSelectionsSatisfied() && hasVariants && (
                <Text
                  type="secondary"
                  style={{
                    display: "block",
                    marginTop: "12px",
                    textAlign: "center",
                    fontSize: "14px",
                  }}
                >
                  Please select a category to continue
                </Text>
              )}

              {/* Additional Information */}
              <div
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: "#f0f8ff",
                  borderRadius: 8,
                }}
              >
                <Text style={{ fontSize: 12 }}>
                  <strong>Note:</strong> This voucher will be added to your cart
                  and can be redeemed later. Terms and conditions may apply.
                </Text>
              </div>
            </div>
          </Col>
        </Row>

        <Footer />
      </div>
    </div>
  );
});

export default VoucherDetails;
